import { birthdayDate, type CalendarItem } from './calendar';
import type { Task } from './model';
import type { RecordLike } from './productivity';

const escapeText = (value: string) =>
  value
    .replaceAll('\\', '\\\\')
    .replaceAll('\n', '\\n')
    .replaceAll(',', '\\,')
    .replaceAll(';', '\\;');

const stamp = (iso: string) => iso.replaceAll(/[-:]/g, '').replace(/\.\d+Z$/, 'Z');

function fold(line: string) {
  const lines: string[] = [];
  let current = '';

  for (const char of line) {
    if (new TextEncoder().encode(current + char).length > 74) {
      lines.push(current);
      current = ' ';
    }

    current += char;
  }

  lines.push(current);

  return lines.join('\r\n');
}

export function calendarICS(records: RecordLike[], year = new Date().getFullYear()) {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Cairn//Calendar Export//EN',
    'CALSCALE:GREGORIAN',
    'X-WR-CALNAME:Cairn',
  ];

  const dtstamp = stamp(new Date().toISOString());

  for (const record of records) {
    if (record.kind === 'calendar') {
      const item = record.value as CalendarItem;

      if (item.type === 'booking' && !item.cancelled) {
        lines.push(
          'BEGIN:VEVENT',
          `UID:${record.id}@cairn`,
          `DTSTAMP:${dtstamp}`,
          `DTSTART:${stamp(item.startsAt)}`,
          `DTEND:${stamp(item.endsAt)}`,
          `SUMMARY:${escapeText(item.title)}`,
          `DESCRIPTION:${escapeText(item.notes)}`,
          'END:VEVENT',
        );
      }

      if (item.type === 'birthday' && !item.cancelled) {
        for (let y = year; y < year + 10; y++) {
          lines.push(
            'BEGIN:VEVENT',
            `UID:${record.id}-${y}@cairn`,
            `DTSTAMP:${dtstamp}`,
            `DTSTART;VALUE=DATE:${birthdayDate(item, y).replaceAll('-', '')}`,
            `SUMMARY:${escapeText(item.title)}`,
            `DESCRIPTION:${escapeText(item.notes)}\\nBirthday export covers ${year}–${year + 9}. Full rules are retained in the workspace JSON.`,
            'END:VEVENT',
          );
        }
      }
    }

    if (record.kind === 'task') {
      const task = record.value as Task;

      if (task.dueDate && !task.dropped) {
        lines.push(
          'BEGIN:VTODO',
          `UID:${record.id}@cairn`,
          `DTSTAMP:${dtstamp}`,
          `DUE;VALUE=DATE:${task.dueDate.replaceAll('-', '')}`,
          `SUMMARY:${escapeText(task.title)}`,
          `STATUS:${task.done ? 'COMPLETED' : 'NEEDS-ACTION'}`,
          'END:VTODO',
        );
      }
    }
  }

  return `${[...lines, 'END:VCALENDAR'].map(fold).join('\r\n')}\r\n`;
}
